import type { SwitchOrderLineToJitResponse } from '@/infrastructure/FulfillmentAdminClient';

/** What the switch to purchase is read from: the line as the order detail shows it. */
export interface LineToJitCandidate {
  lineNo: number;
  totalQuantity: number;
  lineStatus: string;
  sourcingRoute?: string;
  updatedAt?: string | null;
}

/** A line past these has left the shelf or the order, so there is no stock left to call missing. */
const FINISHED_LINE_STATUSES = new Set(['OUTBOUNDED', 'CLOSED', 'CANCELLED', 'RETURNED']);

/** The server switches only a line whose units are counted on a shelf. */
const ON_SHELF_LINE_STATUSES = new Set(['INBOUNDED', 'READY']);

/** The server refuses the switch on an order that has moved past these. */
const OPEN_ORDER_STATUSES = new Set(['ORDERED', 'AWAITING_INBOUND', 'READY']);

/**
 * Whether the line offers "No stock → awaiting purchase" at all (SSD-7506): only an existing-inventory line still on
 * this order. A JIT line already waits for a purchase, and a shipped or cancelled one has nothing on a shelf.
 */
export const offersLineToJit = (line: Pick<LineToJitCandidate, 'sourcingRoute' | 'lineStatus'>): boolean =>
  line.sourcingRoute === 'EXISTING_INVENTORY' && !FINISHED_LINE_STATUSES.has(line.lineStatus);

/** Why the switch is shut on a line that offers it, or '' when it is open. */
export const describeLineToJitBlock = (line: LineToJitCandidate, orderStatus: string): string => {
  if (!ON_SHELF_LINE_STATUSES.has(line.lineStatus)) {
    return `Only an inbounded or ready line holds shelf stock to switch; this line is ${line.lineStatus}`;
  }
  if (!OPEN_ORDER_STATUSES.has(orderStatus)) {
    return `The order is ${orderStatus}; its lines can no longer switch to purchase`;
  }
  return line.updatedAt ? '' : 'This line does not say when it was last changed; reload the order first';
};

/** Refusals that mean the line or shelf on screen is not what the server holds, so the order is re-read. */
export const LINE_TO_JIT_STALE_REFUSALS: ReadonlySet<string> = new Set([
  'LINE_NOT_SWITCHABLE',
  'LINE_HOLD_NOT_ON_SHELF',
  'SWITCH_HOLD_CHANGED',
  'SWITCH_STOCK_BELOW_ZERO',
]);

/**
 * A refused switch in the Order team's words. The server's sentence says what is wrong with the line; a refusal that
 * reloaded the order says so, and a server without the switch says who can turn it on.
 */
export const describeLineToJitRefusal = (
  code: string | undefined,
  message: string | string[] | undefined,
): string | undefined => {
  const sentence = [message ?? []].flat().join(' · ');
  if (code === 'LINE_TO_JIT_NEEDS_MYSQL') {
    return 'Switching a line to purchase is not switched on for this server yet. Ask engineering to enable it.';
  }
  if (code && LINE_TO_JIT_STALE_REFUSALS.has(code) && sentence) {
    return `${sentence.replace(/\.$/, '')}. The order was reloaded.`;
  }
  return sentence || undefined;
};

/** The success sentence: which line now awaits purchase and, on a partial switch, what stayed on the shelf. */
export const describeLineToJitResult = (result: SwitchOrderLineToJitResponse): string => {
  if (result.keptQuantity === 0) {
    return `Line ${result.lineNo} switched to purchase: ${result.quantity} unit(s) now await inbound.`;
  }
  return (
    `${result.quantity} unit(s) of line ${result.sourceLineNo} switched to purchase as line ${result.lineNo}; ` +
    `line ${result.sourceLineNo} keeps ${result.keptQuantity} unit(s) from stock.`
  );
};
